import { useState, useEffect } from 'react';

const TOUR_FORM_ENDPOINT = 'https://readdy.ai/api/form/d7fidrvimhg6tri2vd20';

interface Props {
  open: boolean;
  onClose: () => void;
}

export default function BookTourModal({ open, onClose }: Props) {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    facility: '',
    preferred_date: '',
    message: '',
  });

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setSubmitted(false);
      setError('');
      setForm({ name: '', company: '', email: '', phone: '', facility: '', preferred_date: '', message: '' });
    }
  }, [open]);

  // Lock body scroll when open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const body = new URLSearchParams({
        name: form.name,
        company: form.company,
        email: form.email,
        phone: form.phone,
        facility: form.facility,
        preferred_date: form.preferred_date,
        message: form.message,
      });
      const res = await fetch(TOUR_FORM_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (!res.ok) throw new Error('Failed');
      setSubmitted(true);
    } catch {
      setError('Something went wrong. Please try again or email info@thehubng.com directly.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(4px)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="relative w-full max-w-lg bg-thl-dark border border-thl-border rounded-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-7 pt-7 pb-5 border-b border-thl-border">
          <div>
            <h2 className="text-white font-bold text-xl">Book a Facility Tour</h2>
            <p className="text-white/40 text-sm mt-0.5">We&apos;ll confirm your slot within 2 business hours.</p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-white/40 hover:text-white hover:bg-white/10 transition-all cursor-pointer"
          >
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        {/* Body */}
        <div className="px-7 py-6 max-h-[75vh] overflow-y-auto">
          {submitted ? (
            <div className="text-center py-10">
              <div className="w-14 h-14 flex items-center justify-center rounded-full bg-thl-red/15 border border-thl-red/30 mx-auto mb-4">
                <i className="ri-check-line text-thl-red text-2xl" />
              </div>
              <p className="text-white font-bold text-lg mb-2">Tour Request Sent!</p>
              <p className="text-white/50 text-sm mb-6">Our team will reach out to confirm your visit date and time.</p>
              <button
                onClick={onClose}
                className="btn-primary"
              >
                Close
              </button>
            </div>
          ) : (
            <form
              id="book-tour-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="flex flex-col gap-4"
            >
              <div>
                <label className="text-white/50 text-xs mb-1 block">Full Name *</label>
                <input
                  required
                  name="name"
                  value={form.name}
                  onChange={e => setForm({ ...form, name: e.target.value })}
                  placeholder="Adaeze Okonkwo"
                  className="w-full bg-white/5 border border-thl-border rounded-lg px-4 py-2.5 text-sm text-white placeholder-white/20 focus:outline-none focus:border-thl-red/50"
                />
              </div>

              <div>
                <label className="text-white/50 text-xs mb-1 block">Company / Organisation *</label>
                <input
                  required
                  name="company"
                  value={form.company}
                  onChange={e => setForm({ ...form, company: e.target.value })}
                  placeholder="BrightSkin Nigeria Ltd"
                  className="w-full bg-white/5 border border-thl-border rounded-lg px-4 py-2.5 text-sm text-white placeholder-white/20 focus:outline-none focus:border-thl-red/50"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-white/50 text-xs mb-1 block">Email *</label>
                  <input
                    required
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={e => setForm({ ...form, email: e.target.value })}
                    placeholder="you@brand.ng"
                    className="w-full bg-white/5 border border-thl-border rounded-lg px-4 py-2.5 text-sm text-white placeholder-white/20 focus:outline-none focus:border-thl-red/50"
                  />
                </div>
                <div>
                  <label className="text-white/50 text-xs mb-1 block">Phone *</label>
                  <input
                    required
                    type="tel"
                    name="phone"
                    value={form.phone}
                    onChange={e => setForm({ ...form, phone: e.target.value })}
                    placeholder="+234 800 000 0000"
                    className="w-full bg-white/5 border border-thl-border rounded-lg px-4 py-2.5 text-sm text-white placeholder-white/20 focus:outline-none focus:border-thl-red/50"
                  />
                </div>
              </div>

              <div>
                <label className="text-white/50 text-xs mb-1 block">Preferred Facility *</label>
                <select
                  required
                  name="facility"
                  value={form.facility}
                  onChange={e => setForm({ ...form, facility: e.target.value })}
                  className="w-full bg-white/5 border border-thl-border rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:border-thl-red/50"
                >
                  <option value="">Select a facility</option>
                  <option>THL Oregun Fulfillment Centre — Oregun, Lagos</option>
                  <option>THL Iganmu Fulfillment Centre — Iganmu, Surulere</option>
                  <option>Open to Either</option>
                </select>
              </div>

              <div>
                <label className="text-white/50 text-xs mb-1 block">Preferred Visit Date</label>
                <input
                  type="date"
                  name="preferred_date"
                  value={form.preferred_date}
                  onChange={e => setForm({ ...form, preferred_date: e.target.value })}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full bg-white/5 border border-thl-border rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:border-thl-red/50"
                />
              </div>

              <div>
                <label className="text-white/50 text-xs mb-1 block">Anything We Should Know?</label>
                <textarea
                  name="message"
                  value={form.message}
                  onChange={e => setForm({ ...form, message: e.target.value })}
                  maxLength={500}
                  rows={3}
                  placeholder="e.g. number of people joining, specific areas of interest..."
                  className="w-full bg-white/5 border border-thl-border rounded-lg px-4 py-2.5 text-sm text-white placeholder-white/20 focus:outline-none focus:border-thl-red/50 resize-none"
                />
              </div>

              {error && (
                <p className="text-red-400 text-xs text-center">{error}</p>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="btn-primary w-full mt-1 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting ? 'Sending…' : 'Request Tour Slot →'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
